import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Truck,
  Package,
  Clock,
  Globe,
  CheckCircle,
  ChevronRight,
  Box,
  Warehouse,
} from "lucide-react";

const LogisticsPage = () => {
  const services = [
    {
      icon: Truck,
      title: "Rapid Response",
      description: "Emergency logistics and time-sensitive transport with 72-hour mobilization capability.",
    },
    {
      icon: Package,
      title: "Critical Delivery",
      description: "Airport-to-door delivery for mission-critical cargo and time-sensitive materials.",
    },
    {
      icon: Warehouse,
      title: "Supply Chain",
      description: "Mission-critical supply chain support for government and commercial operations.",
    },
    {
      icon: Globe,
      title: "Global Deployment",
      description: "Rapid deployment anywhere CONUS/OCONUS for expeditionary logistics needs.",
    },
  ];

  const capabilities = [
    "Emergency logistics coordination",
    "Time-sensitive transport",
    "Airport-to-door delivery capability",
    "Critical cargo handling",
    "72-hour mobilization",
    "CONUS/OCONUS deployment",
    "Last mile delivery expertise",
    "Mission-critical supply chain support",
  ];

  return (
    <div className="min-h-screen" data-testid="logistics-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="logistics-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              LOGISTICS SERVICES
            </p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              RAPID RESPONSE & CRITICAL DELIVERY
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed mb-8">
              AMR Global Holdings LLC delivers time-sensitive logistics support with 
              72-hour mobilization capability. Our veteran teams provide emergency 
              logistics, critical cargo handling, and last mile delivery expertise.
            </p>
            <div className="flex items-center gap-4 text-white text-sm">
              <Clock className="w-5 h-5 text-[#c5a04a]" />
              <span>72-Hour Mobilization Capability</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="logistics-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">
              LOGISTICS SERVICES
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-tactical text-center" data-testid={`logistics-service-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mx-auto mb-6">
                    <service.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities List */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="logistics-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1768796373307-fc2f843660f8?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Logistics Operations"
                className="rounded-sm shadow-lg"
              />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">FULL SPECTRUM</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                LOGISTICS CAPABILITIES
              </h2>
              <ul className="space-y-4">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`logistics-capability-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#1a365d]" data-testid="logistics-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-5xl font-heading text-[#c5a04a] mb-2">72</p>
              <p className="text-white text-sm tracking-wider">HOUR MOBILIZATION</p>
            </div>
            <div>
              <p className="text-5xl font-heading text-[#c5a04a] mb-2">24/7</p>
              <p className="text-white text-sm tracking-wider">RAPID RESPONSE</p>
            </div>
            <div>
              <p className="text-5xl font-heading text-[#c5a04a] mb-2">100%</p>
              <p className="text-white text-sm tracking-wider">VETERAN TEAMS</p>
            </div>
            <div>
              <p className="text-5xl font-heading text-[#c5a04a] mb-2">GLOBAL</p>
              <p className="text-white text-sm tracking-wider">DEPLOYMENT</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="logistics-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Box className="w-16 h-16 text-[#1a365d] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
            MISSION-CRITICAL LOGISTICS
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Contact us for time-sensitive logistics requirements. Our veteran teams 
            are ready to mobilize within 72 hours for your mission.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-tactical btn-primary text-sm" data-testid="logistics-contact-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="btn-tactical btn-outline text-sm" data-testid="logistics-services-btn">
                All Services
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LogisticsPage;
